import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  Card,
  Table,
  Button,
  Modal,
  DatePicker,
  Select,
  message,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Progress,
  Timeline,
  Image,
  Empty,
  Badge,
  Tooltip,
  Collapse,
  Descriptions,
  Alert,
  Divider,
  Spin,
  Radio,
  Input,
} from "antd";
import {
  FileTextOutlined,
  EyeOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  TeamOutlined,
  PictureOutlined,
  FileImageOutlined,
  InfoCircleOutlined,
  TrophyOutlined,
  WarningOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Polyline,
  Popup,
  Tooltip as LeafletTooltip,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import api from "../services/api";
import LoadingSpinner from "../components/UI/LoadingSpinner";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const SERVER_BASE_URL = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
const THAI_MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const DAY_LABELS = ["จ", "อ", "พ", "พฤ", "ศ", "ส", "อา"];
const formatDaysShort = (mask) => {
  if (mask == null || mask === 127) return "ทุกวัน";
  if (mask === 0) return "-";
  const days = Array.from({ length: 7 }, (_, i) => i).filter(
    (i) => (mask >> i) & 1,
  );
  if (JSON.stringify(days) === JSON.stringify([0, 1, 2, 3, 4])) return "จ-ศ";
  if (JSON.stringify(days) === JSON.stringify([5, 6])) return "ส-อา";
  return days.map((d) => DAY_LABELS[d]).join(" ");
};

const PatrolReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [guards, setGuards] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedGuard, setSelectedGuard] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [stats, setStats] = useState({});
  const [mapModalVisible, setMapModalVisible] = useState(false);
  const [mapReport, setMapReport] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [printMatrix, setPrintMatrix] = useState(null);
  const [printHourMode, setPrintHourMode] = useState("scheduled");
  const [printSignatures, setPrintSignatures] = useState([
    { role: "ผู้จัดทำ", name: "", title: "หัวหน้าทีมสายตรวจ" },
    { role: "ผู้ตรวจสอบ", name: "", title: "หัวหน้าแผนกรักษาความปลอดภัย" },
    { role: "ผู้รับรายงาน", name: "", title: "ผู้จัดการบริษัท" },
  ]);
  const [rangeExportVisible, setRangeExportVisible] = useState(false);
  const [rangePickerValue, setRangePickerValue] = useState(null);
  const [calendarDates, setCalendarDates] = useState(null);
  const [rangeExportLoading, setRangeExportLoading] = useState(false);

  // Hide rounds not scheduled for the selected day (print report still needs the full list)
  const visibleReports = useMemo(
    () => reports.filter((r) => r.status !== "not_scheduled"),
    [reports],
  );

  // Remove sample data - will use real API

  useEffect(() => {
    if (user?.current_company_id) {
      fetchData();
    }
  }, [selectedDate, selectedRound, selectedGuard, user?.current_company_id]); // Re-fetch when company changes

  const fetchData = async () => {
    try {
      setLoading(true);

      const params = {
        date: selectedDate.format("YYYY-MM-DD"),
        round_id: selectedRound,
        guard_id: selectedGuard,
        limit: 50, // Increase limit for better UX
      };

      const [reportsRes, statsRes, roundsRes, guardsRes] = await Promise.all([
        api.get("/patrol-reports", { params }),
        api.get("/patrol-reports/stats", { params }),
        api.get("/patrol-reports/rounds"),
        api.get("/patrol-reports/guards"),
      ]);

      if (reportsRes.data.success) {
        setReports(reportsRes.data.data);
      }

      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }

      if (roundsRes.data.success) {
        setRounds(roundsRes.data.data);
      }

      if (guardsRes.data.success) {
        setGuards(guardsRes.data.data);
      }
    } catch (error) {
      console.error("Fetch reports error:", error);
      message.error("ไม่สามารถโหลดข้อมูลรายงานได้");
    } finally {
      setLoading(false);
    }
  };

  const showDetail = async (report) => {
    try {
      // Use "all" to get data from all guards in this round
      const guardId = "all";

      // Fetch detailed report data
      const response = await api.get(
        `/patrol-reports/${report.date}/${report.round_id}/${guardId}`,
      );

      if (response.data.success) {
        // Add guard names info to the response for display
        const detailData = {
          ...response.data.data,
          all_guard_names: report.guard_name,
        };
        setSelectedReport(detailData);
        setDetailModalVisible(true);
      } else {
        message.error("ไม่สามารถโหลดรายละเอียดได้");
      }
    } catch (error) {
      console.error("Fetch report detail error:", error);
      message.error("ไม่สามารถโหลดรายละเอียดได้");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "late":
        return "success";
      case "incomplete":
        return "warning";
      case "missed":
        return "error";
      case "not_scheduled":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "สำเร็จ";
      case "late":
        return "สำเร็จ";
      case "incomplete":
        return "ไม่ครบ";
      case "missed":
        return "ไม่ตรวจ";
      case "not_scheduled":
        return "ไม่ใช่วันตรวจ";
      default:
        return "ไม่ทราบ";
    }
  };

  const exportCSV = (data, filename) => {
    const BOM = "\uFEFF";
    const csv = BOM + data;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportAllReports = async () => {
    if (visibleReports.length === 0) {
      message.warning("ไม่มีข้อมูลให้ส่งออก");
      return;
    }

    const hideLoading = message.loading("กำลังส่งออกรายงานทั้งหมด...", 0);

    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Summary
      const summaryHeaders = [
        "วันที่",
        "รอบตรวจ",
        "เวลา",
        "รปภ.",
        "ตรวจแล้ว",
        "ทั้งหมด",
        "ตรงเวลา",
        "เกินเวลา",
        "ไม่ตรวจ",
        "ใช้เวลา(นาที)",
        "สถานะ",
      ];
      const summaryRows = visibleReports.map((r) => [
        dayjs(r.date).format("DD/MM/YYYY"),
        r.round_name,
        `${r.start_time} - ${r.end_time}`,
        r.guard_name || "-",
        r.completed_checkpoints || 0,
        r.total_checkpoints || 0,
        r.completed_on_time || 0,
        r.completed_late || 0,
        r.missed_checkpoints || 0,
        r.duration_minutes || 0,
        getStatusText(r.status),
      ]);
      const summaryWs = XLSX.utils.aoa_to_sheet([
        ["บริษัท", user?.current_company_name || "-"],
        ["วันที่", selectedDate.format("DD/MM/YYYY")],
        [],
        summaryHeaders,
        ...summaryRows,
      ]);
      summaryWs["!cols"] = summaryHeaders.map(() => ({ wch: 16 }));
      XLSX.utils.book_append_sheet(wb, summaryWs, "สรุปรวม");

      // Sheet per round: fetch detail and create sheet
      for (const report of visibleReports) {
        try {
          const response = await api.get(
            `/patrol-reports/${report.date}/${report.round_id}/all`,
          );
          if (!response.data.success) continue;

          const detail = response.data.data;
          const roundEndTime = detail.end_time;

          // Info rows at top
          const infoRows = [
            ["บริษัท", user?.current_company_name || "-"],
            ["รอบตรวจ", detail.round_name],
            ["วันที่", dayjs(detail.date).format("DD/MM/YYYY")],
            ["เวลา", `${detail.start_time} - ${detail.end_time}`],
            ["รปภ.", report.guard_name || "-"],
            ["สถานะ", getStatusText(detail.status)],
            [
              "ตรวจแล้ว",
              `${detail.completed_checkpoints}/${detail.total_checkpoints} จุด`,
            ],
            ["ตรงเวลา", detail.completed_on_time || 0],
            ["เกินเวลา", detail.completed_late || 0],
            ["ไม่ตรวจ", detail.missed_checkpoints || 0],
            [
              "ใช้เวลา",
              detail.duration_minutes ? `${detail.duration_minutes} นาที` : "-",
            ],
            [],
          ];

          const cpHeaders = [
            "ลำดับ",
            "จุดตรวจ",
            "กำหนด",
            "ตรวจจริง",
            "สถานะ",
            "ตรงเวลา/เกินเวลา",
            "หมายเหตุ",
          ];
          const cpRows = detail.checkpoints.map((cp, i) => {
            const isLate =
              cp.status === "completed" &&
              cp.checked_time &&
              roundEndTime &&
              cp.checked_time > roundEndTime;
            return [
              i + 1,
              cp.name,
              roundEndTime ? `ก่อน ${roundEndTime}` : "-",
              cp.checked_time || "-",
              getStatusText(cp.status),
              cp.status === "completed"
                ? isLate
                  ? "เกินเวลา"
                  : "ตรงเวลา"
                : "-",
              cp.notes || "",
            ];
          });

          const sheetData = [...infoRows, cpHeaders, ...cpRows];
          const ws = XLSX.utils.aoa_to_sheet(sheetData);
          ws["!cols"] = [
            { wch: 8 },
            { wch: 24 },
            { wch: 16 },
            { wch: 14 },
            { wch: 14 },
            { wch: 18 },
            { wch: 30 },
          ];

          // Sheet name max 31 chars, no special chars
          const sheetName = (detail.round_name || `รอบ ${report.round_id}`)
            .replace(/[\\/*?[\]:]/g, "")
            .substring(0, 31);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        } catch (err) {
          console.error(
            `Failed to fetch detail for round ${report.round_id}:`,
            err,
          );
        }
      }

      const companySlug = (user?.current_company_name || "")
        .replace(/[\\/*?[\]:]/g, "")
        .trim();
      XLSX.writeFile(
        wb,
        `patrol-reports-${companySlug}-${selectedDate.format("YYYY-MM-DD")}.xlsx`,
      );
      message.success("ส่งออกรายงานสำเร็จ");
    } catch (error) {
      console.error("Export all error:", error);
      message.error("ส่งออกรายงานล้มเหลว");
    } finally {
      hideLoading();
    }
  };

  const exportRangeReports = async (startDate, endDate) => {
    const totalDays = endDate.diff(startDate, "day") + 1;
    setRangeExportLoading(true);
    message.loading({
      content: `กำลังดึงข้อมูล 1/${totalDays} วัน...`,
      key: "range-export",
      duration: 0,
    });

    try {
      const summaryRows = [];
      const detailRows = [];

      for (let i = 0; i < totalDays; i++) {
        const date = startDate.add(i, "day");
        message.loading({
          content: `กำลังดึงข้อมูล ${i + 1}/${totalDays} วัน...`,
          key: "range-export",
          duration: 0,
        });

        try {
          const res = await api.get("/patrol-reports", {
            params: { date: date.format("YYYY-MM-DD"), limit: 50 },
          });

          const dayReports = res.data.success
            ? res.data.data.filter((r) => r.status !== "not_scheduled")
            : [];

          if (dayReports.length === 0) {
            summaryRows.push([
              date.format("DD/MM/YYYY"),
              0,
              0,
              0,
              0,
              0,
              0,
              "-",
              "ไม่มีข้อมูล",
            ]);
            continue;
          }

          const totalRounds = dayReports.length;
          const successCount = dayReports.filter(
            (r) => r.status === "completed" || r.status === "late",
          ).length;
          const totalOnTime = dayReports.reduce(
            (s, r) => s + (r.completed_on_time || 0),
            0,
          );
          const totalLate = dayReports.reduce(
            (s, r) => s + (r.completed_late || 0),
            0,
          );
          const totalMissed = dayReports.reduce(
            (s, r) => s + (r.missed_checkpoints || 0),
            0,
          );
          const rate =
            totalRounds > 0
              ? Math.round((successCount / totalRounds) * 100)
              : 0;

          summaryRows.push([
            date.format("DD/MM/YYYY"),
            totalRounds,
            successCount,
            totalRounds - successCount,
            totalOnTime,
            totalLate,
            totalMissed,
            `${rate}%`,
            "",
          ]);

          dayReports.forEach((r) => {
            detailRows.push([
              date.format("DD/MM/YYYY"),
              r.round_name,
              `${r.start_time} - ${r.end_time}`,
              r.guard_name || "-",
              r.completed_checkpoints || 0,
              r.total_checkpoints || 0,
              r.completed_on_time || 0,
              r.completed_late || 0,
              r.missed_checkpoints || 0,
              r.duration_minutes || 0,
              getStatusText(r.status),
            ]);
          });
        } catch {
          summaryRows.push([
            date.format("DD/MM/YYYY"),
            "-",
            "-",
            "-",
            "-",
            "-",
            "-",
            "-",
            "โหลดไม่สำเร็จ",
          ]);
        }
      }

      const wb = XLSX.utils.book_new();

      const summaryHeaders = [
        "วันที่",
        "รอบทั้งหมด",
        "สำเร็จ",
        "ไม่สำเร็จ",
        "ตรงเวลา",
        "เกินเวลา",
        "ไม่ตรวจ",
        "อัตราสำเร็จ",
        "หมายเหตุ",
      ];
      const summaryWs = XLSX.utils.aoa_to_sheet([
        ["บริษัท", user?.current_company_name || "-"],
        [
          "ช่วงวันที่",
          `${startDate.format("DD/MM/YYYY")} - ${endDate.format("DD/MM/YYYY")}`,
        ],
        ["จำนวน", `${totalDays} วัน`],
        [],
        summaryHeaders,
        ...summaryRows,
      ]);
      summaryWs["!cols"] = summaryHeaders.map(() => ({ wch: 16 }));
      XLSX.utils.book_append_sheet(wb, summaryWs, "สรุปรายวัน");

      if (detailRows.length > 0) {
        const detailHeaders = [
          "วันที่",
          "รอบตรวจ",
          "เวลา",
          "รปภ.",
          "ตรวจแล้ว",
          "ทั้งหมด",
          "ตรงเวลา",
          "เกินเวลา",
          "ไม่ตรวจ",
          "ใช้เวลา(นาที)",
          "สถานะ",
        ];
        const detailWs = XLSX.utils.aoa_to_sheet([
          detailHeaders,
          ...detailRows,
        ]);
        detailWs["!cols"] = detailHeaders.map(() => ({ wch: 16 }));
        XLSX.utils.book_append_sheet(wb, detailWs, "รายละเอียด");
      }

      const companySlug = (user?.current_company_name || "")
        .replace(/[\\/*?[\]:]/g, "")
        .trim();
      XLSX.writeFile(
        wb,
        `patrol-${companySlug}-${startDate.format("YYYY-MM-DD")}_${endDate.format("YYYY-MM-DD")}.xlsx`,
      );
      message.success({ content: "ส่งออกรายงานสำเร็จ", key: "range-export" });
      setRangeExportVisible(false);
      setRangePickerValue(null);
    } catch (error) {
      console.error("Export range error:", error);
      message.error({ content: "ส่งออกรายงานล้มเหลว", key: "range-export" });
    } finally {
      setRangeExportLoading(false);
    }
  };

  const exportSingleReport = async (report) => {
    try {
      const response = await api.get(
        `/patrol-reports/${report.date}/${report.round_id}/all`,
      );

      if (!response.data.success) {
        message.error("ไม่สามารถโหลดข้อมูลสำหรับส่งออกได้");
        return;
      }

      const detail = response.data.data;
      const roundEndTime = detail.end_time;
      const headers = [
        "ลำดับ",
        "จุดตรวจ",
        "กำหนด",
        "ตรวจจริง",
        "สถานะ",
        "ตรงเวลา/เกินเวลา",
        "หมายเหตุ",
        "ละติจูด",
        "ลองจิจูด",
      ];
      const rows = detail.checkpoints.map((cp, i) => {
        const isLate =
          cp.status === "completed" &&
          cp.checked_time &&
          roundEndTime &&
          cp.checked_time > roundEndTime;
        return [
          i + 1,
          cp.name,
          roundEndTime ? `ก่อน ${roundEndTime}` : "-",
          cp.checked_time || "-",
          getStatusText(cp.status),
          cp.status === "completed" ? (isLate ? "เกินเวลา" : "ตรงเวลา") : "-",
          cp.notes || "",
          cp.latitude || "",
          cp.longitude || "",
        ];
      });

      const info = [
        `บริษัท: ${user?.current_company_name || "-"}`,
        `รอบตรวจ: ${detail.round_name}`,
        `วันที่: ${dayjs(detail.date).format("DD/MM/YYYY")}`,
        `เวลา: ${detail.start_time} - ${detail.end_time}`,
        `รปภ.: ${report.guard_name || "-"}`,
        `สถานะ: ${getStatusText(detail.status)}`,
        `ตรวจแล้ว: ${detail.completed_checkpoints}/${detail.total_checkpoints} จุด (ตรงเวลา ${detail.completed_on_time || 0} / เกินเวลา ${detail.completed_late || 0})`,
        "",
      ];

      const csvContent =
        info.join("\n") +
        "\n" +
        [headers, ...rows]
          .map((row) => row.map((cell) => `"${cell}"`).join(","))
          .join("\n");
      const companySlug = (user?.current_company_name || "")
        .replace(/[\\/*?[\]:]/g, "")
        .trim();
      exportCSV(
        csvContent,
        `patrol-${companySlug}-${detail.round_name}-${dayjs(detail.date).format("YYYY-MM-DD")}.csv`,
      );
      message.success("ส่งออกรายงานสำเร็จ");
    } catch (error) {
      console.error("Export error:", error);
      message.error("ส่งออกรายงานล้มเหลว");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "late":
        return <CheckCircleOutlined style={{ color: "#fa8c16" }} />;
      case "incomplete":
        return <ExclamationCircleOutlined style={{ color: "#fa8c16" }} />;
      case "missed":
        return <ClockCircleOutlined style={{ color: "#f5222d" }} />;
      default:
        return <InfoCircleOutlined />;
    }
  };

  const showMap = async (record) => {
    setMapLoading(true);
    setMapModalVisible(true);
    try {
      const response = await api.get(
        `/patrol-reports/${record.date}/${record.round_id}/all`,
      );
      if (response.data.success) {
        setMapReport({
          ...response.data.data,
          all_guard_names: record.guard_name,
        });
      } else {
        message.error("ไม่สามารถโหลดข้อมูลแผนที่ได้");
        setMapModalVisible(false);
      }
    } catch (error) {
      console.error("Fetch map data error:", error);
      message.error("ไม่สามารถโหลดข้อมูลแผนที่ได้");
      setMapModalVisible(false);
    } finally {
      setMapLoading(false);
    }
  };

  const getCheckpointMapColor = (cp, endTime) => {
    if (cp.status === "completed") {
      const isLate = cp.checked_time && endTime && cp.checked_time > endTime;
      return isLate ? "#fa8c16" : "#52c41a";
    }
    if (cp.status === "missed") return "#f5222d";
    return "#bfbfbf";
  };

  const getCheckpointMapLabel = (cp, endTime) => {
    if (cp.status === "completed") {
      const isLate = cp.checked_time && endTime && cp.checked_time > endTime;
      return isLate ? "เกินเวลา" : "ตรงเวลา";
    }
    if (cp.status === "missed") return "ไม่ตรวจ";
    return "ยังไม่ตรวจ";
  };

  const displayHours = useMemo(() => {
    if (!printMatrix) return [];
    if (printHourMode === "all") return Array.from({ length: 24 }, (_, i) => i);
    return printMatrix.scheduledHours;
  }, [printMatrix, printHourMode]);

  const openPrintModal = async () => {
    if (reports.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับสร้างรายงาน");
      return;
    }
    setPrintModalVisible(true);
    setPrintLoading(true);
    setPrintMatrix(null);
    try {
      const checkpointMap = new Map();
      const matrix = {};
      const scheduledHoursSet = new Set();
      const notScheduledHoursSet = new Set();
      const roundCheckpoints = {};
      const roundEndTimes = {};
      for (const report of reports) {
        let roundHour = null;
        if (report.start_time) {
          roundHour = parseInt(report.start_time.split(":")[0]);
          scheduledHoursSet.add(roundHour);
          roundEndTimes[roundHour] = report.end_time;
          if (report.status === "not_scheduled") {
            notScheduledHoursSet.add(roundHour);
          }
        }
        let detail;
        try {
          const res = await api.get(
            `/patrol-reports/${report.date}/${report.round_id}/all`,
          );
          if (!res.data.success) continue;
          detail = res.data.data;
        } catch {
          continue;
        }
        detail.checkpoints.forEach((cp) => {
          if (!checkpointMap.has(cp.name)) {
            checkpointMap.set(cp.name, { name: cp.name, id: cp.id });
          }
          if (roundHour !== null) {
            if (!roundCheckpoints[roundHour])
              roundCheckpoints[roundHour] = new Set();
            roundCheckpoints[roundHour].add(cp.name);
          }
        });
        detail.checkpoints.forEach((cp) => {
          if (!matrix[cp.name]) matrix[cp.name] = {};
          if (
            cp.checked_time &&
            (cp.status === "completed" || cp.status === "late")
          ) {
            const hour = parseInt(cp.checked_time.split(":")[0]);
            if (!matrix[cp.name][hour]) {
              matrix[cp.name][hour] = {
                time: cp.checked_time.substring(0, 5),
                isLate: cp.status === "late",
              };
            }
          }
        });
      }
      const checkpointOrder = [...checkpointMap.values()];
      const scheduledHours = [...scheduledHoursSet].sort((a, b) => a - b);
      const activeHours = scheduledHours.filter(
        (h) => !notScheduledHoursSet.has(h),
      );
      const fmt = (h) => `${String(h).padStart(2, "0")}.00 น.`;
      const scheduledTimesText =
        activeHours.length > 1
          ? activeHours.slice(0, -1).map(fmt).join(", ") +
            " และ" +
            fmt(activeHours[activeHours.length - 1])
          : activeHours.map(fmt).join(", ");
      setPrintMatrix({
        checkpoints: checkpointOrder,
        matrix,
        scheduledHours,
        notScheduledHours: [...notScheduledHoursSet],
        scheduledTimesText,
        totalCheckpoints: checkpointOrder.length,
        roundCheckpoints,
        roundEndTimes,
      });
    } catch (e) {
      console.error("Print matrix error:", e);
      message.error("ไม่สามารถสร้างรายงานได้");
      setPrintModalVisible(false);
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePrint = () => {
    if (!printMatrix) return;
    const hoursToShow =
      printHourMode === "all"
        ? Array.from({ length: 24 }, (_, i) => i)
        : printMatrix.scheduledHours;
    const printDateStr = `${selectedDate.date()} ${THAI_MONTHS[selectedDate.month()]} ${selectedDate.year() + 543}`;
    const notSchedSet = new Set(printMatrix.notScheduledHours || []);
    const headerCells = hoursToShow
      .map((h) =>
        notSchedSet.has(h)
          ? `<th class="ns-h">${String(h).padStart(2, "0")}</th>`
          : `<th>${String(h).padStart(2, "0")}</th>`,
      )
      .join("");
    const bodyRows = printMatrix.checkpoints
      .map((cp, i) => {
        const cells = hoursToShow
          .map((h) => {
            if (notSchedSet.has(h)) return `<td class="ns">—</td>`;
            const t = printMatrix.matrix[cp.name]?.[h];
            if (t)
              return `<td class="${t.isLate ? "late" : "tc"}">${t.time}</td>`;
            const inRound = printMatrix.roundCheckpoints[h]?.has(cp.name);
            if (!inRound) return `<td class="ns">⊗</td>`;
            const endTime = printMatrix.roundEndTimes[h];
            const isPast =
              !selectedDate.isSame(dayjs(), "day") ||
              (() => {
                if (!endTime) return true;
                const [eh, em] = endTime.split(":").map(Number);
                const now = dayjs();
                return (
                  now.hour() > eh || (now.hour() === eh && now.minute() >= em)
                );
              })();
            return `<td class="${isPast ? "missed" : "pending"}">${isPast ? "⊗" : "⊙"}</td>`;
          })
          .join("");
        return `<tr class="${i % 2 === 0 ? "" : "odd"}"><td class="cn">${String(i + 1).padStart(2, "0")}.${cp.name}</td>${cells}</tr>`;
      })
      .join("");
    const sigHtml = printSignatures
      .map(
        (sig) =>
          `<div class="sb"><div class="sl"><span>ลงชื่อ</span><span class="sline"></span><span>${sig.role}</span></div><div class="sn">${sig.name || ""}</div><div class="st">${sig.title || ""}</div></div>`,
      )
      .join("");
    const totalCols = hoursToShow.length + 1;
    const pw = window.open("", "_blank", "width=1400,height=900");
    pw.document.write(`<!DOCTYPE html><html><head>
<meta charset="utf-8">
<title>รายงานการตรวจ QR CODE</title>
<style>
@page{
  size:A4 landscape;
  margin:10mm 12mm 10mm;
  @bottom-center{
    content:"( " counter(page) " / " counter(pages) " )";
    font-size:9pt;
    color:#444;
    font-family:'Sarabun','Tahoma','Arial',sans-serif;
  }
}
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Sarabun','Tahoma','Arial',sans-serif;font-size:10pt;color:#000}
.tb{display:flex;justify-content:space-between;font-size:9pt;margin-bottom:4px}
.ttl{text-align:center;margin-bottom:6px}
.ttl h2{font-size:14pt;font-weight:bold}
.ttl h3{font-size:12pt;font-weight:normal}
.sh{display:flex;justify-content:space-between;font-size:9pt}
.shr{text-align:right}
table{width:100%;border-collapse:collapse;font-size:9pt}
th.dh{background:#fff;color:#000;border:none;padding:0 0 6px 0;border-bottom:2px solid #1890ff}
th{background:#1890ff;color:#fff;border:1px solid #096dd9;padding:3px 4px;text-align:center;font-weight:bold}
td{border:1px solid #d0d0d0;padding:2px 4px}
td.cn{text-align:left;white-space:nowrap;font-weight:500}
td.tc{text-align:center;font-size:8pt;background:#f6ffed;color:#389e0d;font-weight:600}
td.late{text-align:center;font-size:8pt;background:#fff7e6;color:#d46b08;font-weight:600}
td.missed{text-align:center;background:#fff1f0;color:#ff4d4f;font-weight:600;font-size:11pt}
td.pending{text-align:center;background:#e6f7ff;color:#1890ff;font-weight:600;font-size:11pt}
td.ns{text-align:center;color:#d9d9d9;font-size:11pt;background:#fafafa}
th.ns-h{background:#1890ff;color:#fff;border-color:#096dd9}
tr.odd{background:#f9f9f9}
.sigs{display:flex;justify-content:space-around;margin-top:16px;page-break-inside:avoid;page-break-before:avoid}
.sb{text-align:center;width:28%}
.sl{display:flex;align-items:flex-end;margin-bottom:4px;font-size:9pt}
.sline{flex:1;border-bottom:1px solid #000;margin:0 3px;min-width:60px}
.sn{font-size:9pt;margin-top:2px}
.st{font-size:8pt;color:#444}
thead{display:table-header-group}
.legend{display:flex;gap:12px;align-items:center;margin-top:5px;flex-wrap:wrap;font-size:8pt}
.li{display:flex;align-items:center;gap:4px}
.lb{width:14px;height:14px;border-radius:2px;border:1px solid rgba(0,0,0,.15);flex-shrink:0}
@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body>
<table>
<thead>
<tr><th class="dh" colspan="${totalCols}">
<div class="tb"><span>${selectedDate.format("D/M/") + (selectedDate.year() + 543)}</span><span>Security System</span></div>
<div class="ttl"><h2>รายงานการตรวจ QR CODE</h2><h3>ประจำ บริษัท ${user?.current_company_name || "-"}</h3></div>
<div class="sh"><span>วันที่ ${printDateStr}</span><div class="shr"><div>สแกนเวลา ${printMatrix.scheduledTimesText}</div><div>จำนวน ${printMatrix.totalCheckpoints} จุด</div></div></div>
<div class="legend">
  <span style="font-weight:600;color:#555">สัญลักษณ์ :</span>
  <div class="li"><div class="lb" style="background:#f6ffed;border-color:#b7eb8f"></div><span style="color:#389e0d">ตรวจตรงเวลา</span></div>
  <div class="li"><div class="lb" style="background:#fff7e6;border-color:#ffd591"></div><span style="color:#d46b08">ตรวจล่าช้า</span></div>
  <div class="li"><div class="lb" style="background:#fff1f0;border-color:#ffccc7"></div><span style="color:#ff4d4f">⊗ ไม่ได้ตรวจ</span></div>
  <div class="li"><div class="lb" style="background:#e6f7ff;border-color:#91d5ff"></div><span style="color:#1890ff">⊙ รอตรวจ</span></div>
  <div class="li"><div class="lb" style="background:#fafafa;border-color:#d9d9d9"></div><span style="color:#bbb">⊗ ไม่ใช่รอบตรวจ</span></div>
  <div class="li"><div class="lb" style="background:#fafafa;border-color:#d9d9d9;display:flex;align-items:center;justify-content:center;color:#bbb;font-size:10pt;font-weight:bold">—</div><span style="color:#8c8c8c;font-style:italic">ไม่มีรอบตรวจวันนี้</span></div>
</div>
</th></tr>
<tr><th style="text-align:left;min-width:170px">จุดสแกน</th>${headerCells}</tr>
</thead>
<tbody>${bodyRows}</tbody>
</table>
<div class="sigs">${sigHtml}</div>
</body></html>`);
    pw.document.close();
    pw.focus();
    setTimeout(() => pw.print(), 400);
  };

  const columns = [
    {
      title: "วันที่",
      dataIndex: "date",
      key: "date",
      render: (date) => <Text strong>{dayjs(date).format("DD/MM/YYYY")}</Text>,
      width: 100,
    },
    {
      title: "รอบตรวจ",
      key: "round_info",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ color: "#1890ff" }}>
            {record.round_name}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.start_time} - {record.end_time}
          </Text>
          <Tag
            color={
              record.days_of_week == null || record.days_of_week === 127
                ? "blue"
                : "purple"
            }
            style={{ fontSize: 11 }}
          >
            {formatDaysShort(record.days_of_week)}
          </Tag>
        </Space>
      ),
      width: 200,
    },
    {
      title: "รปภ.",
      dataIndex: "guard_name",
      key: "guard_name",
      render: (names) => {
        if (!names || names === "ยังไม่มีรปภ.ตรวจ") {
          return (
            <Space>
              <TeamOutlined style={{ color: "#d9d9d9" }} />
              <Text type="secondary">{names}</Text>
            </Space>
          );
        }

        const guardList = names
          .split(", ")
          .filter((name) => name.trim() !== "รปภ.ไม่ทราบชื่อ");

        if (guardList.length === 0) {
          return (
            <Space>
              <TeamOutlined style={{ color: "#d9d9d9" }} />
              <Text type="secondary">ยังไม่มีรปภ.ตรวจ</Text>
            </Space>
          );
        }

        if (guardList.length === 1) {
          return (
            <Space>
              <TeamOutlined style={{ color: "#fa8c16" }} />
              <Text>{guardList[0]}</Text>
            </Space>
          );
        }

        return (
          <Space direction="vertical" size={2}>
            <Space>
              <TeamOutlined style={{ color: "#fa8c16" }} />
              <Text strong>{guardList.length} คน</Text>
            </Space>
            <Text style={{ fontSize: 11, color: "#666" }}>
              {guardList.join(", ")}
            </Text>
          </Space>
        );
      },
      width: 160,
    },
    {
      title: "ความคืบหน้า",
      key: "progress",
      render: (_, record) => {
        // Calculate different checkpoint types
        const completedOnTime = record.completed_on_time || 0;
        const completedLate = record.completed_late || 0;
        const missedCheckpoints = record.missed_checkpoints || 0;
        const totalCheckpoints = record.total_checkpoints || 1;

        // Calculate percentages for each segment
        const onTimePercent = (completedOnTime / totalCheckpoints) * 100;
        const latePercent = (completedLate / totalCheckpoints) * 100;
        const missedPercent = (missedCheckpoints / totalCheckpoints) * 100;

        return (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            {/* Multi-color progress bar */}
            <div
              style={{
                position: "relative",
                height: 6,
                backgroundColor: "#f0f0f0",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              {/* Green segment - on time */}
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  width: `${onTimePercent}%`,
                  height: "100%",
                  backgroundColor: "#52c41a",
                  transition: "width 0.3s ease",
                }}
              />
              {/* Orange segment - late */}
              <div
                style={{
                  position: "absolute",
                  left: `${onTimePercent}%`,
                  width: `${latePercent}%`,
                  height: "100%",
                  backgroundColor: "#fa8c16",
                  transition: "width 0.3s ease",
                }}
              />
              {/* Gray segment represents unchecked - no need to explicitly show */}
            </div>

            {/* Progress text with color coding */}
            <div
              style={{
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <Text style={{ fontWeight: 500 }}>
                {record.completed_checkpoints || 0}/{totalCheckpoints} จุด
              </Text>

              {completedOnTime > 0 && (
                <span
                  style={{
                    color: "#52c41a",
                    fontSize: 11,
                    backgroundColor: "#f6ffed",
                    padding: "1px 4px",
                    borderRadius: 3,
                    border: "1px solid #b7eb8f",
                  }}
                >
                  ตรงเวลา {completedOnTime}
                </span>
              )}

              {completedLate > 0 && (
                <span
                  style={{
                    color: "#fa8c16",
                    fontSize: 11,
                    backgroundColor: "#fff7e6",
                    padding: "1px 4px",
                    borderRadius: 3,
                    border: "1px solid #ffd591",
                  }}
                >
                  เกินเวลา {completedLate}
                </span>
              )}

              {missedCheckpoints > 0 && (
                <span
                  style={{
                    color: "#f5222d",
                    fontSize: 11,
                    backgroundColor: "#fff2f0",
                    padding: "1px 4px",
                    borderRadius: 3,
                    border: "1px solid #ffccc7",
                  }}
                >
                  ไม่ตรวจ {missedCheckpoints}
                </span>
              )}
            </div>
          </Space>
        );
      },
      width: 240,
    },
    {
      title: "ใช้เวลา",
      key: "duration",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.duration_minutes ? (
            <>
              <Text strong>
                {Math.floor(record.duration_minutes / 60)}:
                {String(record.duration_minutes % 60).padStart(2, "0")} ชม.
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                เริ่ม: {dayjs(record.started_at).format("HH:mm")}
                {record.finished_at &&
                  ` - ${dayjs(record.finished_at).format("HH:mm")}`}
              </Text>
            </>
          ) : (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
      width: 120,
      align: "center",
    },
    {
      title: "สถานะ",
      key: "status",
      render: (_, record) => (
        <Tag
          color={getStatusColor(record.status)}
          icon={getStatusIcon(record.status)}
        >
          {getStatusText(record.status)}
        </Tag>
      ),
      width: 100,
      align: "center",
    },
    {
      title: "การจัดการ",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="ดูรายละเอียด">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => showDetail(record)}
            />
          </Tooltip>
          <Tooltip title="แผนที่เส้นทาง">
            <Button
              type="text"
              icon={<EnvironmentOutlined />}
              onClick={() => showMap(record)}
              disabled={
                record.completed_checkpoints === 0 ||
                (dayjs().format("YYYY-MM-DD") === record.date &&
                  dayjs().format("HH:mm:ss") < record.end_time)
              }
            />
          </Tooltip>
          <Tooltip title="ส่งออกรายงาน">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => exportSingleReport(record)}
            />
          </Tooltip>
        </Space>
      ),
      width: 140,
      align: "center",
    },
  ];

  if (loading) {
    return <LoadingSpinner tip="กำลังโหลดรายงาน..." />;
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title
            level={2}
            style={{ margin: 0, display: "flex", alignItems: "center", gap: 8 }}
          >
            <FileTextOutlined style={{ color: "#1890ff" }} />
            รายงานการตรวจรอบ
          </Title>
          <Paragraph
            type="secondary"
            style={{ margin: "8px 0 0 0", fontSize: 14 }}
          >
            รายงานความครบถ้วนและรายละเอียดการตรวจรอบของรปภ.
          </Paragraph>
        </Col>
        <Col>
          <Space>
            {user?.role === "multi_company_admin" && (
              <Button icon={<PrinterOutlined />} onClick={openPrintModal}>
                พิมพ์รายงานประจำวัน
              </Button>
            )}
            {(user?.role === "multi_company_admin" ||
              user?.role === "supervisor") && (
              <Button
                icon={<DownloadOutlined />}
                onClick={() => setRangeExportVisible(true)}
              >
                ส่งออกรายงาน
              </Button>
            )}
            {(user?.role === "multi_company_admin" ||
              user?.role === "supervisor") && (
              <Button icon={<DownloadOutlined />} onClick={exportAllReports}>
                ส่งออก Excel วันที่เลือก
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="รอบทั้งหมด"
              value={stats.totalRounds || 0}
              prefix={<CalendarOutlined />}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="สำเร็จครบถ้วน"
              value={stats.completedRounds || 0}
              prefix={<TrophyOutlined />}
              suffix={`/ ${stats.totalRounds || 0}`}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="อัตราความสำเร็จ"
              value={stats.averageCompletionRate || 0}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{
                color:
                  (stats.averageCompletionRate || 0) >= 80
                    ? "#52c41a"
                    : "#fa8c16",
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card size="small">
            <Statistic
              title="เวลาเฉลี่ย/รอบ"
              value={stats.averageDuration || 0}
              suffix="นาที"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card
        title={
          <Space>
            <Text strong style={{ fontSize: 16 }}>
              🔍 ตัวกรองข้อมูล
            </Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
        bodyStyle={{ paddingTop: 16 }}
      >
        <Row gutter={[24, 16]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#1890ff", fontSize: 13 }}>
                <CalendarOutlined style={{ marginRight: 4 }} />
                วันที่
              </Text>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                format="DD/MM/YYYY"
                placeholder="เลือกวันที่"
                allowClear={false}
                style={{ width: "100%" }}
                disabledDate={(current) => {
                  return current && current > dayjs().endOf("day");
                }}
              />
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#1890ff", fontSize: 13 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                รอบตรวจ
              </Text>
              <Select
                placeholder="ทุกรอบตรวจ"
                style={{ width: "100%" }}
                value={selectedRound}
                onChange={setSelectedRound}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {rounds.map((round) => (
                  <Option key={round.id} value={round.id}>
                    <Space>
                      <Text>{round.name}</Text>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        ({round.start_time} - {round.end_time})
                      </Text>
                    </Space>
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#1890ff", fontSize: 13 }}>
                <TeamOutlined style={{ marginRight: 4 }} />
                รปภ.
              </Text>
              <Select
                placeholder="รปภ.ทุกนาย"
                style={{ width: "100%" }}
                value={selectedGuard}
                onChange={setSelectedGuard}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {guards
                  .filter((g) => g.id)
                  .map((guard) => (
                    <Option key={guard.id} value={guard.id}>
                      {guard.name}
                    </Option>
                  ))}
              </Select>
            </Space>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong style={{ color: "#1890ff", fontSize: 13 }}>
                ⚡ การจัดการ
              </Text>
              <Space.Compact style={{ width: "100%" }}>
                <Button
                  onClick={() => {
                    setSelectedDate(dayjs());
                    setSelectedRound(null);
                    setSelectedGuard(null);
                  }}
                  style={{ flex: 1 }}
                  title="รีเซ็ตตัวกรอง"
                >
                  รีเซ็ต
                </Button>
                <Button
                  type="primary"
                  onClick={() => message.info("กำลังรีเฟรชข้อมูล...")}
                  style={{ flex: 1 }}
                  title="รีเฟรชข้อมูล"
                >
                  รีเฟรช
                </Button>
              </Space.Compact>
            </Space>
          </Col>
        </Row>

        {/* Quick Filters */}
        <Divider style={{ margin: "16px 0 12px 0" }} />
        <Row justify="space-between" align="middle">
          <Col>
            <Space wrap>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ช่วงเวลา:
              </Text>
              <Button
                size="small"
                type={
                  selectedDate.isSame(dayjs(), "day") ? "primary" : "default"
                }
                onClick={() => setSelectedDate(dayjs())}
              >
                วันนี้
              </Button>
              <Button
                size="small"
                type={
                  selectedDate.isSame(dayjs().subtract(1, "day"), "day")
                    ? "primary"
                    : "default"
                }
                onClick={() => setSelectedDate(dayjs().subtract(1, "day"))}
              >
                เมื่อวาน
              </Button>
              <Button
                size="small"
                onClick={() => setSelectedDate(dayjs().subtract(7, "days"))}
              >
                7 วันที่แล้ว
              </Button>
            </Space>
          </Col>
          <Col>
            <Space>
              <Text type="secondary" style={{ fontSize: 12 }}>
                📊 ข้อมูลที่แสดง: {visibleReports.length} รายการ
              </Text>
              {selectedDate && !selectedDate.isSame(dayjs(), "day") && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  | วันที่เลือก: {selectedDate.format("DD/MM/YYYY")}
                </Text>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Reports Table */}
      <Card>
        {visibleReports.length > 0 ? (
          <Table
            columns={columns}
            dataSource={visibleReports}
            rowKey={(record) => `${record.round_id}-${record.date}`}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} จาก ${total} รายการ`,
            }}
            locale={{
              emptyText: <Empty description="ไม่พบรายงาน" />,
            }}
            scroll={{ x: 1200 }}
          />
        ) : (
          <Empty
            description="ไม่พบรายงานในวันที่เลือก"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            รายละเอียดการตรวจรอบ
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ปิด
          </Button>,
          <Button
            key="export"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() =>
              selectedReport &&
              exportSingleReport({
                date: selectedReport.date,
                round_id: selectedReport.round_id,
                round_name: selectedReport.round_name,
                guard_name:
                  selectedReport.all_guard_names || selectedReport.guard_name,
              })
            }
          >
            ส่งออกรายงาน
          </Button>,
        ]}
        width={1000}
        style={{ top: 20 }}
      >
        {selectedReport && (
          <div>
            {/* Report Header */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="รอบตรวจ">
                      <Text strong style={{ color: "#1890ff" }}>
                        {selectedReport.round_name}
                      </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="วันที่">
                      {dayjs(selectedReport.date).format("DD/MM/YYYY")}
                    </Descriptions.Item>
                    <Descriptions.Item label="รปภ.">
                      {selectedReport.all_guard_names ||
                        selectedReport.guard_name}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
                <Col span={12}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="สถานะ">
                      <Tag
                        color={getStatusColor(selectedReport.status)}
                        icon={getStatusIcon(selectedReport.status)}
                      >
                        {getStatusText(selectedReport.status)}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="เวลาที่ใช้">
                      {selectedReport.duration_minutes
                        ? `${Math.floor(
                            selectedReport.duration_minutes / 60,
                          )}:${String(
                            selectedReport.duration_minutes % 60,
                          ).padStart(2, "0")} ชม.`
                        : "ยังไม่เสร็จสิ้น"}
                    </Descriptions.Item>
                    <Descriptions.Item label="ความครบถ้วน">
                      {(() => {
                        // Calculate different checkpoint types for detail modal
                        const completedOnTime =
                          selectedReport.completed_on_time || 0;
                        const completedLate =
                          selectedReport.completed_late || 0;
                        const totalCheckpoints =
                          selectedReport.total_checkpoints || 1;

                        // Calculate percentages for each segment
                        const onTimePercent =
                          (completedOnTime / totalCheckpoints) * 100;
                        const latePercent =
                          (completedLate / totalCheckpoints) * 100;

                        return (
                          <Space
                            direction="vertical"
                            size={4}
                            style={{ width: "100%" }}
                          >
                            {/* Multi-color progress bar */}
                            <div
                              style={{
                                position: "relative",
                                height: 8,
                                backgroundColor: "#f0f0f0",
                                borderRadius: 4,
                                overflow: "hidden",
                                width: "200px",
                              }}
                            >
                              {/* Green segment - on time */}
                              <div
                                style={{
                                  position: "absolute",
                                  left: 0,
                                  width: `${onTimePercent}%`,
                                  height: "100%",
                                  backgroundColor: "#52c41a",
                                  transition: "width 0.3s ease",
                                }}
                              />
                              {/* Orange segment - late */}
                              <div
                                style={{
                                  position: "absolute",
                                  left: `${onTimePercent}%`,
                                  width: `${latePercent}%`,
                                  height: "100%",
                                  backgroundColor: "#fa8c16",
                                  transition: "width 0.3s ease",
                                }}
                              />
                            </div>

                            {/* Progress text with color coding */}
                            <div
                              style={{
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <Text style={{ fontWeight: 500 }}>
                                {selectedReport.completed_checkpoints || 0}/
                                {totalCheckpoints} จุด (
                                {selectedReport.completion_rate}%)
                              </Text>

                              {completedOnTime > 0 && (
                                <span
                                  style={{
                                    color: "#52c41a",
                                    fontSize: 11,
                                    backgroundColor: "#f6ffed",
                                    padding: "1px 4px",
                                    borderRadius: 3,
                                    border: "1px solid #b7eb8f",
                                  }}
                                >
                                  ตรงเวลา {completedOnTime}
                                </span>
                              )}

                              {completedLate > 0 && (
                                <span
                                  style={{
                                    color: "#fa8c16",
                                    fontSize: 11,
                                    backgroundColor: "#fff7e6",
                                    padding: "1px 4px",
                                    borderRadius: 3,
                                    border: "1px solid #ffd591",
                                  }}
                                >
                                  เกินเวลา {completedLate}
                                </span>
                              )}
                            </div>
                          </Space>
                        );
                      })()}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>

            {/* Summary Alert */}
            {selectedReport.missed_checkpoints > 0 && (
              <Alert
                message="พบจุดตรวจที่ไม่ได้ตรวจสอบ"
                description={`มีจุดตรวจที่ไม่ได้ตรวจสอบ ${selectedReport.missed_checkpoints} จุดจากทั้งหมด ${selectedReport.total_checkpoints} จุด`}
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {/* Checkpoints Timeline */}
            <Card
              size="small"
              title={
                <Space>
                  <EnvironmentOutlined />
                  รายละเอียดจุดตรวจ (
                  {selectedReport.total_checkpoints ||
                    selectedReport.checkpoints.length}{" "}
                  จุด)
                </Space>
              }
            >
              <Timeline mode="left">
                {selectedReport.checkpoints.map((checkpoint, index) => {
                  const isLate =
                    checkpoint.status === "late" ||
                    (checkpoint.status === "completed" &&
                      checkpoint.checked_time &&
                      selectedReport.end_time &&
                      checkpoint.checked_time > selectedReport.end_time);

                  return (
                    <Timeline.Item
                      key={checkpoint.id}
                      color={
                        checkpoint.status === "completed"
                          ? isLate
                            ? "orange"
                            : "green"
                          : checkpoint.status === "missed"
                            ? "red"
                            : "orange"
                      }
                      dot={getStatusIcon(isLate ? "late" : checkpoint.status)}
                    >
                      <Card
                        size="small"
                        style={{
                          marginBottom: 8,
                          borderLeft: isLate ? "3px solid #fa8c16" : undefined,
                        }}
                      >
                        <Row gutter={16}>
                          <Col span={16}>
                            <Space direction="vertical" size={4}>
                              <Text strong style={{ fontSize: 16 }}>
                                {index + 1}. {checkpoint.name}
                              </Text>

                              <Space>
                                {selectedReport.end_time && (
                                  <>
                                    <Text type="secondary">กำหนด:</Text>
                                    <Text>ก่อน {selectedReport.end_time}</Text>
                                  </>
                                )}
                                {checkpoint.checked_time && (
                                  <>
                                    <Text type="secondary">| ตรวจจริง:</Text>
                                    <Text
                                      strong
                                      style={{
                                        color: isLate ? "#fa8c16" : "#52c41a",
                                      }}
                                    >
                                      {checkpoint.checked_time}
                                    </Text>
                                  </>
                                )}
                              </Space>

                              <Space>
                                <Tag color={getStatusColor(checkpoint.status)}>
                                  {getStatusText(checkpoint.status)}
                                </Tag>
                                {isLate && <Tag color="orange">เกินเวลา</Tag>}
                                {checkpoint.images.length > 0 && (
                                  <Tag icon={<PictureOutlined />} color="blue">
                                    {checkpoint.images.length} รูป
                                  </Tag>
                                )}
                              </Space>

                              {checkpoint.notes && (
                                <Text
                                  style={{
                                    fontStyle: "italic",
                                    color: "#666",
                                    backgroundColor: "#f9f9f9",
                                    padding: "4px 8px",
                                    borderRadius: 4,
                                    display: "block",
                                    marginTop: 4,
                                  }}
                                >
                                  📝 {checkpoint.notes}
                                </Text>
                              )}

                              {checkpoint.latitude && checkpoint.longitude && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                  📍 {Number(checkpoint.latitude).toFixed(6)},{" "}
                                  {Number(checkpoint.longitude).toFixed(6)}
                                </Text>
                              )}
                            </Space>
                          </Col>

                          <Col span={8}>
                            {checkpoint.images.length > 0 ? (
                              <Space direction="vertical" size={4}>
                                <Text strong style={{ fontSize: 12 }}>
                                  รูปภาพที่ถ่าย:
                                </Text>
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 4,
                                  }}
                                >
                                  {checkpoint.images.map((img, imgIndex) => (
                                    <Image
                                      key={
                                        img.id ||
                                        img.url ||
                                        `img-${checkpoint.id}-${imgIndex}`
                                      }
                                      width={60}
                                      height={60}
                                      src={
                                        img.url.startsWith("http")
                                          ? img.url
                                          : `${SERVER_BASE_URL}${img.url}`
                                      }
                                      style={{
                                        objectFit: "cover",
                                        borderRadius: 4,
                                        border: "1px solid #d9d9d9",
                                      }}
                                      placeholder={
                                        <div
                                          style={{
                                            width: 60,
                                            height: 60,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#f0f0f0",
                                          }}
                                        >
                                          <FileImageOutlined />
                                        </div>
                                      }
                                    />
                                  ))}
                                </div>
                              </Space>
                            ) : checkpoint.status === "completed" ? (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                ไม่มีรูปภาพ
                              </Text>
                            ) : (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                ไม่ได้ตรวจสอบ
                              </Text>
                            )}
                          </Col>
                        </Row>
                      </Card>
                    </Timeline.Item>
                  );
                })}
              </Timeline>
            </Card>
          </div>
        )}
      </Modal>

      {/* Map Modal */}
      <Modal
        title={
          <Space>
            <EnvironmentOutlined />
            แผนที่เส้นทางตรวจ
          </Space>
        }
        open={mapModalVisible}
        onCancel={() => {
          setMapModalVisible(false);
          setMapReport(null);
        }}
        footer={null}
        width={900}
        style={{ top: 20 }}
        destroyOnClose
      >
        {mapLoading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#999" }}>
              กำลังโหลดข้อมูลแผนที่...
            </div>
          </div>
        ) : mapReport ? (
          (() => {
            const checkpointsWithCoords = mapReport.checkpoints.filter(
              (cp) => cp.latitude && cp.longitude,
            );

            if (checkpointsWithCoords.length === 0) {
              return <Empty description="ไม่มีข้อมูลพิกัดจุดตรวจ" />;
            }

            const completedSorted = mapReport.checkpoints
              .filter(
                (cp) =>
                  cp.status === "completed" &&
                  cp.latitude &&
                  cp.longitude &&
                  cp.checked_time,
              )
              .sort((a, b) => a.checked_time.localeCompare(b.checked_time));

            const polylinePositions = completedSorted.map((cp) => [
              parseFloat(cp.latitude),
              parseFloat(cp.longitude),
            ]);

            const bounds = checkpointsWithCoords.map((cp) => [
              parseFloat(cp.latitude),
              parseFloat(cp.longitude),
            ]);

            return (
              <div>
                <style>{`
                .checkpoint-number-tooltip.leaflet-tooltip {
                  background: transparent !important;
                  border: none !important;
                  box-shadow: none !important;
                  padding: 0 !important;
                  margin: 0 !important;
                }
                .checkpoint-number-tooltip.leaflet-tooltip::before {
                  display: none !important;
                }
              `}</style>

                {/* Header info */}
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Text type="secondary">รอบตรวจ: </Text>
                      <Text strong style={{ color: "#1890ff" }}>
                        {mapReport.round_name}
                      </Text>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">เวลา: </Text>
                      <Text>
                        {mapReport.start_time} - {mapReport.end_time}
                      </Text>
                    </Col>
                    <Col span={8}>
                      <Text type="secondary">วันที่: </Text>
                      <Text>{dayjs(mapReport.date).format("DD/MM/YYYY")}</Text>
                    </Col>
                  </Row>
                </Card>

                {/* Legend */}
                <div
                  style={{
                    marginBottom: 12,
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    สัญลักษณ์:
                  </Text>
                  {[
                    { color: "#52c41a", label: "ตรงเวลา" },
                    { color: "#fa8c16", label: "เกินเวลา" },
                    { color: "#f5222d", label: "ไม่ตรวจ" },
                    { color: "#bfbfbf", label: "ยังไม่ตรวจ" },
                  ].map((item) => (
                    <Space key={item.label} size={4}>
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          backgroundColor: item.color,
                        }}
                      />
                      <Text style={{ fontSize: 12 }}>{item.label}</Text>
                    </Space>
                  ))}
                  <Space size={4}>
                    <div
                      style={{
                        width: 24,
                        height: 3,
                        backgroundColor: "#1890ff",
                        borderRadius: 2,
                      }}
                    />
                    <Text style={{ fontSize: 12 }}>เส้นทางตรวจ</Text>
                  </Space>
                </div>

                {/* Map */}
                <div
                  style={{
                    height: 450,
                    marginBottom: 16,
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #d9d9d9",
                  }}
                >
                  <MapContainer
                    {...(bounds.length > 1
                      ? { bounds, boundsOptions: { padding: [30, 30] } }
                      : { center: bounds[0], zoom: 16 })}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {/* Polyline connecting completed checkpoints */}
                    {polylinePositions.length > 1 && (
                      <Polyline
                        positions={polylinePositions}
                        color="#1890ff"
                        weight={3}
                        opacity={0.7}
                        dashArray="8, 8"
                      />
                    )}

                    {/* Checkpoint markers */}
                    {checkpointsWithCoords.map((cp) => {
                      const seqNum = mapReport.checkpoints.indexOf(cp) + 1;
                      return (
                        <CircleMarker
                          key={cp.id}
                          center={[
                            parseFloat(cp.latitude),
                            parseFloat(cp.longitude),
                          ]}
                          radius={14}
                          fillColor={getCheckpointMapColor(
                            cp,
                            mapReport.end_time,
                          )}
                          color="#fff"
                          weight={2}
                          fillOpacity={0.9}
                        >
                          <LeafletTooltip
                            permanent
                            direction="center"
                            className="checkpoint-number-tooltip"
                          >
                            <span
                              style={{
                                fontWeight: "bold",
                                fontSize: 11,
                                color: "#fff",
                              }}
                            >
                              {seqNum}
                            </span>
                          </LeafletTooltip>
                          <Popup>
                            <div style={{ minWidth: 160 }}>
                              <div
                                style={{ fontWeight: "bold", marginBottom: 4 }}
                              >
                                {seqNum}. {cp.name}
                              </div>
                              <div>
                                สถานะ:{" "}
                                <span
                                  style={{
                                    color: getCheckpointMapColor(
                                      cp,
                                      mapReport.end_time,
                                    ),
                                    fontWeight: "bold",
                                  }}
                                >
                                  {getCheckpointMapLabel(
                                    cp,
                                    mapReport.end_time,
                                  )}
                                </span>
                              </div>
                              {cp.checked_time && (
                                <div>เวลาตรวจ: {cp.checked_time}</div>
                              )}
                              {cp.checked_by && (
                                <div>ตรวจโดย: {cp.checked_by}</div>
                              )}
                            </div>
                          </Popup>
                        </CircleMarker>
                      );
                    })}
                  </MapContainer>
                </div>

                {/* Checkpoint list */}
                <Table
                  size="small"
                  dataSource={mapReport.checkpoints}
                  rowKey="id"
                  pagination={false}
                  scroll={{ y: 200 }}
                  columns={[
                    {
                      title: "ลำดับ",
                      key: "seq",
                      width: 60,
                      align: "center",
                      render: (_, __, i) => i + 1,
                    },
                    {
                      title: "จุดตรวจ",
                      dataIndex: "name",
                      ellipsis: true,
                    },
                    {
                      title: "เวลาตรวจ",
                      dataIndex: "checked_time",
                      width: 100,
                      render: (time) => time || <Text type="secondary">-</Text>,
                    },
                    {
                      title: "ตรวจโดย",
                      dataIndex: "checked_by",
                      width: 120,
                      render: (name) => name || <Text type="secondary">-</Text>,
                    },
                    {
                      title: "สถานะ",
                      key: "map_status",
                      width: 100,
                      align: "center",
                      render: (_, cp) => (
                        <Tag
                          color={
                            cp.status === "completed"
                              ? cp.checked_time &&
                                mapReport.end_time &&
                                cp.checked_time > mapReport.end_time
                                ? "orange"
                                : "green"
                              : cp.status === "missed"
                                ? "red"
                                : "default"
                          }
                        >
                          {getCheckpointMapLabel(cp, mapReport.end_time)}
                        </Tag>
                      ),
                    },
                  ]}
                />
              </div>
            );
          })()
        ) : null}
      </Modal>

      {/* Print Modal */}
      <Modal
        title={
          <Space>
            <PrinterOutlined />
            รายงานสรุปประจำวัน
          </Space>
        }
        open={printModalVisible}
        onCancel={() => setPrintModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPrintModalVisible(false)}>
            ปิด
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={!printMatrix || printLoading}
          >
            พิมพ์
          </Button>,
        ]}
        width="90vw"
        style={{ top: 16 }}
        bodyStyle={{
          padding: "16px 24px",
          maxHeight: "85vh",
          overflowY: "auto",
        }}
      >
        {/* Controls */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space align="center" style={{ marginBottom: 12 }}>
            <Text strong>แสดงคอลัมน์:</Text>
            <Radio.Group
              value={printHourMode}
              onChange={(e) => setPrintHourMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="scheduled">เฉพาะรอบที่ตรวจ</Radio.Button>
              <Radio.Button value="all">ทั้ง 24 ชั่วโมง</Radio.Button>
            </Radio.Group>
          </Space>
          <Divider style={{ margin: "0 0 12px" }} />
          <Row gutter={[16, 8]}>
            {printSignatures.map((sig, i) => (
              <Col key={i} xs={24} sm={8}>
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Text strong style={{ fontSize: 12 }}>
                    {sig.role}:
                  </Text>
                  <Select
                    style={{ width: "100%" }}
                    placeholder="เลือก รปภ./พนักงาน"
                    allowClear
                    showSearch
                    size="small"
                    value={sig.name || undefined}
                    onChange={(val) => {
                      const updated = [...printSignatures];
                      updated[i] = { ...updated[i], name: val || "" };
                      setPrintSignatures(updated);
                    }}
                  >
                    {guards.map((g) => (
                      <Option key={g.name} value={g.name}>
                        {g.name}
                      </Option>
                    ))}
                  </Select>
                  <Input
                    placeholder="ตำแหน่ง/หน้าที่"
                    size="small"
                    value={sig.title}
                    onChange={(e) => {
                      const updated = [...printSignatures];
                      updated[i] = { ...updated[i], title: e.target.value };
                      setPrintSignatures(updated);
                    }}
                  />
                </Space>
              </Col>
            ))}
          </Row>
        </Card>

        {/* Preview */}
        {printLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin tip="กำลังสร้างรายงาน..." />
          </div>
        ) : printMatrix ? (
          <div
            style={{
              background: "#fff",
              border: "1px solid #d9d9d9",
              borderTop: "3px solid #1890ff",
              borderRadius: 4,
              padding: "20px 24px",
              fontFamily: "'Sarabun', Tahoma, Arial, sans-serif",
            }}
          >
            {/* Report header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                marginBottom: 4,
              }}
            >
              <span>
                {selectedDate.format("D/M/") + (selectedDate.year() + 543)}
              </span>
              <span>Security System</span>
            </div>
            <div style={{ textAlign: "center", marginBottom: 6 }}>
              <div style={{ fontSize: 16, fontWeight: "bold" }}>
                รายงานการตรวจ QR CODE
              </div>
              <div style={{ fontSize: 13 }}>
                ประจำ บริษัท {user?.current_company_name || "-"}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 12,
                margin: "6px 0 10px",
              }}
            >
              <div>
                {`วันที่ ${selectedDate.date()} ${THAI_MONTHS[selectedDate.month()]} ${selectedDate.year() + 543}`}
              </div>
              <div style={{ textAlign: "right" }}>
                <div>สแกนเวลา {printMatrix.scheduledTimesText}</div>
                <div>จำนวน {printMatrix.totalCheckpoints} จุด</div>
              </div>
            </div>

            {/* Matrix Table */}
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 11,
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid #096dd9",
                        padding: "6px 10px",
                        background: "#1890ff",
                        color: "#fff",
                        textAlign: "left",
                        minWidth: 170,
                        fontWeight: 600,
                      }}
                    >
                      จุดสแกน
                    </th>
                    {displayHours.map((h) => (
                      <th
                        key={h}
                        style={{
                          border: "1px solid #096dd9",
                          padding: "6px 4px",
                          background: "#1890ff",
                          color: "#fff",
                          textAlign: "center",
                          minWidth: 44,
                          fontWeight: 600,
                        }}
                      >
                        {String(h).padStart(2, "0")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {printMatrix.checkpoints.map((cp, i) => (
                    <tr
                      key={cp.name}
                      style={{ background: i % 2 === 0 ? "#fff" : "#f9f9f9" }}
                    >
                      <td
                        style={{
                          border: "1px solid #d0d0d0",
                          padding: "4px 10px",
                          whiteSpace: "nowrap",
                          fontWeight: 500,
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}.{cp.name}
                      </td>
                      {displayHours.map((h) => {
                        const isNotSched = (
                          printMatrix.notScheduledHours || []
                        ).includes(h);
                        if (isNotSched) {
                          return (
                            <td
                              key={h}
                              style={{
                                border: "1px solid #d0d0d0",
                                padding: "4px",
                                textAlign: "center",
                                fontSize: 12,
                                color: "#bbb",
                                background: "#fafafa",
                              }}
                            >
                              —
                            </td>
                          );
                        }
                        const t = printMatrix.matrix[cp.name]?.[h];
                        if (t) {
                          return (
                            <td
                              key={h}
                              style={{
                                border: "1px solid #d0d0d0",
                                padding: "4px",
                                textAlign: "center",
                                fontSize: 10,
                                background: t.isLate ? "#fff7e6" : "#f6ffed",
                                color: t.isLate ? "#d46b08" : "#389e0d",
                                fontWeight: 600,
                              }}
                            >
                              {t.time}
                            </td>
                          );
                        }
                        const inRound = printMatrix.roundCheckpoints[h]?.has(
                          cp.name,
                        );
                        if (!inRound) {
                          return (
                            <td
                              key={h}
                              style={{
                                border: "1px solid #d0d0d0",
                                padding: "4px",
                                textAlign: "center",
                                fontSize: 12,
                                color: "#d9d9d9",
                                background: "#fafafa",
                              }}
                            >
                              ⊗
                            </td>
                          );
                        }
                        const endTime = printMatrix.roundEndTimes[h];
                        const isPast =
                          !selectedDate.isSame(dayjs(), "day") ||
                          (() => {
                            if (!endTime) return true;
                            const [eh, em] = endTime.split(":").map(Number);
                            const now = dayjs();
                            return (
                              now.hour() > eh ||
                              (now.hour() === eh && now.minute() >= em)
                            );
                          })();
                        return (
                          <td
                            key={h}
                            style={{
                              border: "1px solid #d0d0d0",
                              padding: "4px",
                              textAlign: "center",
                              fontSize: 12,
                              background: isPast ? "#fff1f0" : "#e6f7ff",
                              color: isPast ? "#ff4d4f" : "#1890ff",
                              fontWeight: 500,
                            }}
                          >
                            {isPast ? "⊗" : "⊙"}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Signatures */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-around",
                marginTop: 28,
              }}
            >
              {printSignatures.map((sig, i) => (
                <div key={i} style={{ textAlign: "center", width: "28%" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      marginBottom: 4,
                      fontSize: 12,
                    }}
                  >
                    <span style={{ whiteSpace: "nowrap" }}>ลงชื่อ</span>
                    <span
                      style={{
                        flex: 1,
                        borderBottom: "1px solid #000",
                        margin: "0 4px",
                        minWidth: 50,
                      }}
                    />
                    <span style={{ whiteSpace: "nowrap" }}>{sig.role}</span>
                  </div>
                  <div style={{ fontSize: 12, marginTop: 2 }}>
                    {sig.name || ""}
                  </div>
                  <div style={{ fontSize: 11, color: "#666" }}>{sig.title}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Modal>
      {/* Range Export Modal */}
      {(user?.role === "multi_company_admin" ||
        user?.role === "supervisor") && (
        <Modal
          title={
            <Space>
              <DownloadOutlined />
              ส่งออกรายงานตามช่วงวันที่
            </Space>
          }
          open={rangeExportVisible}
          onCancel={() => {
            if (!rangeExportLoading) {
              setRangeExportVisible(false);
              setRangePickerValue(null);
              setCalendarDates(null);
            }
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setRangeExportVisible(false);
                setRangePickerValue(null);
                setCalendarDates(null);
              }}
              disabled={rangeExportLoading}
            >
              ยกเลิก
            </Button>,
            <Button
              key="export"
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => {
                if (rangePickerValue?.[0] && rangePickerValue?.[1]) {
                  exportRangeReports(rangePickerValue[0], rangePickerValue[1]);
                } else {
                  message.warning("กรุณาเลือกช่วงวันที่ก่อน");
                }
              }}
              loading={rangeExportLoading}
              disabled={!rangePickerValue?.[0] || !rangePickerValue?.[1]}
            >
              ส่งออก Excel
            </Button>,
          ]}
          width={520}
          destroyOnClose
        >
          <div style={{ padding: "8px 0" }}>
            <Space direction="vertical" style={{ width: "100%" }} size={12}>
              <div>
                <Text strong style={{ color: "#1890ff", fontSize: 13 }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  ช่วงวันที่ (สูงสุด 31 วัน, เลือกได้เฉพาะย้อนหลัง)
                </Text>
                <div style={{ marginTop: 8 }}>
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    value={rangePickerValue}
                    onChange={(dates) => {
                      setRangePickerValue(dates);
                      if (!dates) setCalendarDates(null);
                    }}
                    onCalendarChange={(dates) => setCalendarDates(dates)}
                    onOpenChange={(open) => {
                      if (open) setCalendarDates(null);
                    }}
                    disabledDate={(current) => {
                      if (current >= dayjs().startOf("day")) return true;
                      if (calendarDates?.[0] && !calendarDates?.[1]) {
                        const start = calendarDates[0];
                        return (
                          current < start.subtract(30, "days") ||
                          current > start.add(30, "days")
                        );
                      }
                      return false;
                    }}
                    format="DD/MM/YYYY"
                    allowClear
                    placeholder={["วันเริ่มต้น", "วันสิ้นสุด"]}
                    disabled={rangeExportLoading}
                  />
                </div>
              </div>

              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  ช่วงด่วน:
                </Text>
                <Space wrap size={4} style={{ marginTop: 6 }}>
                  {[
                    {
                      label: "เมื่อวาน",
                      start: dayjs().subtract(1, "day"),
                      end: dayjs().subtract(1, "day"),
                    },
                    {
                      label: "7 วันล่าสุด",
                      start: dayjs().subtract(7, "day"),
                      end: dayjs().subtract(1, "day"),
                    },
                    {
                      label: "30 วันล่าสุด",
                      start: dayjs().subtract(30, "day"),
                      end: dayjs().subtract(1, "day"),
                    },
                    {
                      label: "เดือนที่แล้ว",
                      start: dayjs().subtract(1, "month").startOf("month"),
                      end: dayjs().subtract(1, "month").endOf("month"),
                    },
                  ].map(({ label, start, end }) => (
                    <Button
                      key={label}
                      size="small"
                      disabled={rangeExportLoading}
                      onClick={() => setRangePickerValue([start, end])}
                    >
                      {label}
                    </Button>
                  ))}
                </Space>
              </div>

              {rangePickerValue?.[0] &&
                rangePickerValue?.[1] &&
                (() => {
                  const days =
                    rangePickerValue[1].diff(rangePickerValue[0], "day") + 1;
                  return (
                    <Alert
                      type={days > 15 ? "warning" : "info"}
                      showIcon
                      message={
                        days > 15
                          ? `เลือก ${days} วัน — อาจใช้เวลาสักครู่ระหว่างส่งออก`
                          : `เลือก ${days} วัน`
                      }
                    />
                  );
                })()}
            </Space>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PatrolReports;
